import { useState, useEffect, useRef } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { readCookie, loadScript, removeScript } from "../../utils/helpers";
import { verifyOtp, updatePhoneNumber, initiateVerification } from "../../pages/verify-user/actions";
import { getTokenExIframeJs, getTenantGroupId, currentDomain } from "../../utils/general-config";
import { counter } from "@fortawesome/fontawesome-svg-core";
import OtpResendTimer from "../otp-resend-timer/component";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { DomainEnum } from "../../services/enum/environment-enum";
import flags from 'react-phone-number-input/flags'

const isValidPhoneNumber = (value) => {
  const parsed = parsePhoneNumberFromString(value || "");
  return parsed ? parsed.isValid() : false;
};

const VerifyByPhone = ({
  toggleModal,
  isLoading,
  setIsLoading,
  userGuid,
  otp,
  toSetOtp,
  onPhoneVerified,
  handleInitiateContactVerification,
}) => {
  // Use 'step' to toggle between the phone input (1) and OTP input (2)
  const [step, setStep] = useState(1); // Start at Step 1 (Phone Number Input)
  const [phoneNumber, setPhoneNumber] = useState("");
  const [error, setError] = useState(""); // error message // Helper function to simulate phone verification API call
  const [countryCode, setCountryCode] = useState("+91");
  const [tokenizePhone, setTokenizePhone] = useState("");
  const [phoneWithCountryCode, setPhoneWithCountryCode] = useState("");
  const isSignSimpli = currentDomain() === DomainEnum.SIGNSIMPLI;
  const logoSrc = isSignSimpli
    ? "/static/images/logos/signsimpli-new-logo.svg"
    : "/static/images/logos/pdfsimpli-new-logo.svg";

  // small key to force remount when necessary
  const [phoneKey, setPhoneKey] = useState(0);
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const wrapperRef = useRef(null);
  const caretRef = useRef({ start: null, end: null, wasFocused: false });

  const findInnerInput = () => {
    // wrapperRef points to PhoneInput wrapper DOM node (set on outer div)
    const wrapper = wrapperRef.current;
    if (!wrapper) return null;
    // PhoneInput renders a native <input>; query for it
    return wrapper.querySelector("input");
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      const input = findInnerInput();
      if (!input) return;

      if (caretRef.current.wasFocused) {
        try {
          input.focus();
          if (typeof caretRef.current.start === "number") {
            input.setSelectionRange(caretRef.current.start, caretRef.current.end);
          }
        } catch (e) {
          // ignore if browser doesn't allow selection set for some reason
        }
      }
      caretRef.current = { start: null, end: null, wasFocused: false };
    });
  }, [phoneKey, step]);

  let tokenProcessed = false;

  useEffect(() => {
    loadScript(getTokenExIframeJs());
    return () => {
      removeScript(getTokenExIframeJs());
    };
  }, [step]);

  useEffect(() => {
    // make React’s setter accessible to external JS
    window.setReactPhoneNumber = (val) => setPhoneNumber(val);
  }, []);

  const simulateSendCode = (phoneNumber) => {
    return new Promise((resolve) => {
      console.log(`Sending code to: ${phoneNumber}`);
      setTimeout(() => {
        resolve(true); // Always succeed in simulation
      }, 1500);
    });
  }; // Helper function to simulate OTP verification API call

  const onSendCode = async () => {
    // if (!phoneNumber || phoneNumber.length < 10) {
    //   setError("Please enter a valid phone number.");
    //   return;
    // }

    const parsed = parsePhoneNumberFromString(phoneWithCountryCode);
    if (!parsed || !parsed.isValid()) {
      setError("Please enter a valid phone number.");
      return;
    }

    setError("");
    setIsLoading(true);
    window.reactFunction = async () => {
      if (tokenProcessed) return;
      const token = localStorage.getItem("token");
      setTokenizePhone(token);
      tokenProcessed = true;
      if (!!token) {
        let data = {
          phoneNumber: token,
          countryCode: countryCode,
        };
        const success = await updatePhoneNumber(data)
          .then(async (response) => {
            console.log("✅ Phone number updated:", response);
            if (response) {
              const payload = {
                UserGuid: userGuid || readCookie("userguid"),
                ContactType: 2,
                TenantGroupId: getTenantGroupId(),
              };
              const success = await initiateVerification(payload);
              setIsLoading(false);
              tokenProcessed = false;
              if (success) {
                setStep(2); // Move to OTP input screen
              } else {
                setError("Failed to send code. Please try again.");
              }
            } else {
              tokenProcessed = false;
              setError("Failed to update phone number. Please try again.");
            }
          })
          .catch((error) => {
            console.error("❌ Phone number update failed:", error);
            tokenProcessed = false;
            return false;
          });
      }

      console.log("Token from TokenEx:", token);
      //localStorage.removeItem("token");
    };
    setIsLoading(false);
    // const success = await handleInitiateContactVerification("phone");

    // setIsLoading(false);
    // if (success) {
    //   setStep(2); // Move to OTP input screen
    // } else {
    //   setError("Failed to send code. Please try again.");
    // }
  }; // --- Step 2: OTP Input Logic (Your original code, adapted) ---

  const handleChange = (e, index) => {
    const value = e.target.value.replace(/[^0-9]/g, ""); // allow only digits
    const newOtp = [...otp];
    newOtp[index] = value;
    toSetOtp(newOtp); // move to next input automatically

    if (index < 5 && value) {
      document.getElementById(`otp-input-${index + 1}`).focus();
    }
    if (newOtp.every((digit) => digit !== "") && newOtp.length === 6) {
      // Trigger the verification immediately
      onVerifyOtp(newOtp);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text");

    const digits = paste.replace(/[^0-9]/g, "").slice(0, 6);
    const newOtp = [...otp]; // Start with the current state

    for (let i = 0; i < digits.length; i++) {
      newOtp[i] = digits[i];
    }
    toSetOtp(newOtp);

    const nextFocusIndex = digits.length < 6 ? digits.length : 5;

    if (document.getElementById(`otp-input-${nextFocusIndex}`)) {
      document.getElementById(`otp-input-${nextFocusIndex}`).focus();
    }

    if (digits.length === 6) {
      onVerifyOtp(newOtp);
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-input-${index - 1}`).focus();
    }
  };

  const onVerifyOtp = async (newOtp) => {
    let code = otp.join("");
    let modifiedOtp = otp;
    if (typeof newOtp === "object" && newOtp !== null) {
      code = newOtp.join("");
      modifiedOtp = newOtp;
    }
    if (code.length < 6) {
      setError("Please enter the 6-digit code.");
      return;
    }

    setError("");
    setIsLoading(true);

    const trimmedOtp = modifiedOtp.join("").trim();
    try {
      const success = await verifyOtp(userGuid || readCookie("userguid"), trimmedOtp);
      console.log("✅ OTP verified:", success);
      if (!success) {
        setError("The verification code you entered is invalid");
        toSetOtp(Array(6).fill("")); // Clear OTP on failure
      } else {
        onPhoneVerified();
        setError("");
        toSetOtp(Array(6).fill(""));
      }
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      // show toast / error message
      console.error("OTP verification error:", error);
    }
  };
  // --- Common Header ---
  const Header = (
    <div className="border-b border-gray-200 relative">
      {/* Close Button */}
      {!isSignSimpli && (
        <div className="absolute top-0 right-0 pt-4 pr-4 z-20">
          <button
            type="button"
            className="rounded-md text-gray-400 hover:text-gray-600"
            onClick={() => toggleModal(false)}
          >
            <span className="sr-only">Close</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      )}
      <div className="flex pl-6 py-2">
        <img
          className="pdf-simpli-logo flex w-36 sm:w-40 lg:w-44 h-7 sm:h-9 lg:h-10"
          src={process.env.PUBLIC_URL + logoSrc}
          width="180"
          alt="pdfsimpli Logo"
        />
      </div>
    </div>
  );

  // --- Common Footer ---
  const Footer = (
    <div className="py-4 mt-4 md:mt-6 text-center text-xs text-gray-500 md:border-t md:border-gray-200">
      <span className="inline-flex items-center">
        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="12" viewBox="0 0 10 12" fill="none">
          <path
            d="M8.37483 5.33217H7.85893V3.7845C7.85893 3.19695 7.71205 2.6524 7.41827 2.15083C7.1245 1.64927 6.72683 1.25161 6.22527 0.957832C5.72371 0.66406 5.17916 0.517174 4.59161 0.517174C4.00407 0.517174 3.45951 0.66406 2.95795 0.957832C2.45639 1.25161 2.05872 1.64927 1.76495 2.15083C1.47118 2.6524 1.32429 3.19695 1.32429 3.7845V5.33217H0.808397C0.52179 5.33217 0.278174 5.43249 0.0775488 5.63311C-0.123076 5.83374 -0.223389 6.07735 -0.223389 6.36396V10.4911C-0.223389 10.7777 -0.123076 11.0213 0.0775488 11.222C0.278174 11.4226 0.52179 11.5229 0.808397 11.5229H8.37483C8.66143 11.5229 8.90505 11.4226 9.10567 11.222C9.3063 11.0213 9.40661 10.7777 9.40661 10.4911V6.36396C9.40661 6.07735 9.3063 5.83374 9.10567 5.63311C8.90505 5.43249 8.66143 5.33217 8.37483 5.33217ZM6.13929 5.33217H3.04393V3.7845C3.04393 3.35458 3.1944 2.98916 3.49534 2.68822C3.79628 2.38729 4.1617 2.23682 4.59161 2.23682C5.02152 2.23682 5.38695 2.38729 5.68788 2.68822C5.98882 2.98916 6.13929 3.35458 6.13929 3.7845V5.33217Z"
            fill="#64C897"
          />
        </svg>
        <span className="ml-2 text-[#16283D] text-[10px]"> Your information is encrypted and secure</span>
      </span>
    </div>
  );
  const handleResend = async () => {
    try {
      const payload = {
        UserGuid: userGuid || readCookie("userguid"),
        ContactType: 2,
        TenantGroupId: getTenantGroupId(),
      };
      const success = await initiateVerification(payload);
      if (!success) {
        setError("Failed to send OTP");
        toSetOtp(Array(6).fill("")); // Clear OTP on failure
      } else {
        setError("");
        toSetOtp(Array(6).fill(""));
      }

      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      // show toast / error message
      console.error("Failed to send error:", error);
    }
  };

  const handlePhoneChange = (val) => {
    setIsEditingPhone(false);
    const value = val || "";
    const digitsOnly = value.replace(/\D/g, "");
    if (digitsOnly.length > 15) {
      // capture caret/focus info BEFORE we change state
      const input = findInnerInput();
      if (input) {
        caretRef.current.wasFocused = document.activeElement === input;
        try {
          caretRef.current.start = input.selectionStart;
          caretRef.current.end = input.selectionEnd;
        } catch {
          caretRef.current.start = null;
          caretRef.current.end = null;
        }
      } else {
        caretRef.current.wasFocused = false;
        caretRef.current.start = null;
        caretRef.current.end = null;
      }

      // create trimmed digits and reconstruct a simple +digits string
      const trimmedDigits = digitsOnly.slice(0, 15);
      const newValue = "+" + trimmedDigits;

      // update state and increment key to force remount
      setPhoneWithCountryCode(newValue);
      // increment key to force remount and therefore re-sync the inner input value
      setPhoneKey((k) => k + 1);

      // do not proceed with parse etc. here — use the effect below or next onChange
      return;
    }

    setPhoneWithCountryCode(value);
    setError("");

    if (value) {
      const parsed = parsePhoneNumberFromString(value);
      if (parsed) {
        setCountryCode("+" + parsed.countryCallingCode);
        if (window.elements?.iframe) {
          window.elements.iframe.setPAN(parsed.nationalNumber);
        }
        setPhoneNumber(parsed.nationalNumber || "");
      } else {
        setCountryCode("");
        setPhoneNumber("");
      }
    } else {
      setCountryCode("");
      setPhoneNumber("");
    }
  };

  const cookieCountry = readCookie?.("country");
  const countryCD = typeof cookieCountry === "string" && cookieCountry.trim() !== "" ? cookieCountry : "US";

  // --- Component Render ---
  return (
    <>
      {Header}

      <div>
        <div className={`px-6 pt-6 relative ${isSignSimpli ? "font-rethink" : ""}`}>
          {/* W-Logo Icon (Red background) */}
          <div className="flex justify-center mb-4">
            <div className="flex items-center justify-center w-14 h-14 rounded-full">
              {isSignSimpli ? (
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="60" height="60" rx="30" fill="#1D7FFF" />
                  <path
                    d="M26.2793 31.4171L32.6579 37.7957L48.2502 22.2035"
                    stroke="#EAEBED"
                    strokeWidth="4.96"
                  />
                  <path d="M11.75 28.9372L20.2549 37.7965L35.8471 22.2042" stroke="white" strokeWidth="4.96" />
                </svg>
              ) : (
                <svg width="80" height="80" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="80" height="80" rx="40" fill="#17AE06" />
                  <path
                    d="M35.0388 41.8895L43.5437 50.3943L64.3333 29.6047"
                    stroke="#EAEBED"
                    strokeWidth="6.61333"
                  />
                  <path d="M15.6667 38.5828L27.0066 50.3951L47.7962 29.6054" stroke="white" strokeWidth="6.61333" />
                </svg>
              )}
            </div>
          </div>

          {step === 1 ? (
            // --- STEP 1: Phone Number Input (image_29858e.png) ---
            <>
              <h2 className={`text-center text-[20px] md:text-2xl font-bold text-[#16283D] ${isSignSimpli ? "font-rethink" : ""}`}>Verify your phone</h2>
              <p className={`mt-2 text-center  text-[#687585] ${isSignSimpli ? "text-[14px] font-normal" : "text-sm"}`}>
                Step <span className={`${isSignSimpli ? "font-semibold" : " "}`}>2</span> of <span className={`${isSignSimpli ? "font-semibold" : " "}`}>2</span>: Please enter your phone number
                <br /> to receive a 6-digit verification code
              </p>

              <div className={` flex justify-center ${isSignSimpli ? "mt-4" : "mt-6"}`}>
                <div id="tokenExIframePhoneDiv" style={{ height: "49.33px" }} className="d-none"></div>
                <div className="flex gap-2 items-center justify-center">
                  <div ref={wrapperRef} className="flex-1">
                    <PhoneInput
                      key={phoneKey}
                      flags={flags}
                      international
                      defaultCountry={countryCD}
                      value={phoneWithCountryCode}
                      onChange={handlePhoneChange}
                      maxLength={17}
                      countryCallingCodeEditable={false}
                      className={`phone-input w-full text-base border rounded-[8px] pl-2 ${
                        error ? "border-red-500" : "border-gray-300"
                      }`}
                      placeholder="Phone number"
                    />
                  </div>
                  {!isSignSimpli && isEditingPhone && (
                    <button
                      id="PhoneCancwel"
                      type="button"
                      onClick={() => {
                        setIsEditingPhone(false);
                        setStep(2);
                      }}
                      className="px-2 py-3 text-gray-500 text-sm font-bold rounded-[8px]  hover:to-red-600 uppercase transition-colors duration-200 ease-in-out whitespace-nowrap"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke-width="1.5"
                        stroke="currentColor"
                        aria-hidden="true"
                        data-slot="icon"
                        class="h-6 w-6"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  )}
                </div>
                {/* <input
                type="tel"
                placeholder="Phone number"
                value={phoneNumber}
                maxLength={10}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^0-9]/g, "");
                  setPhoneNumber(value);
                  setError("");
                  if (window.elements?.iframe) {
                    window.elements.iframe.setPAN(value);
                    }
                    }}
                    className={`w-full max-w-xs px-4 py-3 border rounded-md text-base focus:ring-2 focus:ring-orange-500 ${
                      error ? "border-red-500" : "border-gray-300"
                      }`}
                      /> */}
              </div>
            </>
          ) : (
            // --- STEP 2: OTP Input (image_2985ab.png) ---
            <>
              <div className="text-center">
                <h2 className="text-center text-lg font-semibold text-gray-900">Check your phone</h2>
                <div className="flex gap-2 items-center justify-center mt-4 max-w-xs mx-auto">
                  <input
                    type="number"
                    value={phoneNumber || "000-000-0000"}
                    readOnly
                    className={`${isSignSimpli ? "w-[250px]" : ""} flex-1 rounded-[8px] border border-[#E5E7EB] py-3 px-4 text-base text-[#16283D] text-center focus:outline-none focus:border-orange-500 focus:ring-1 focus:ring-orange-500`}
                  />
                  <button
                    id="PhoneS"
                    type="button"
                    onClick={() => {
                      setIsEditingPhone(true);
                      setStep(1);
                    }}
                    className="text-black-500transition-colors duration-200 ease-in-out flex items-center justify-center "
                  >
                    <svg
                      width="40"
                      height="50"
                      viewBox="0 0 19 54"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      preserveAspectRatio="xMidYMid meet"
                    >
                      <path
                        d="M12.1933 21.3451L15.14 24.2918M13.4433 20.0951C13.8341 19.7044 14.3641 19.4849 14.9167 19.4849C15.4693 19.4849 15.9992 19.7044 16.39 20.0951C16.7808 20.4859 17.0003 21.0159 17.0003 21.5685C17.0003 22.1211 16.7808 22.6511 16.39 23.0418L4.91667 34.5151H2V31.5385L13.4433 20.0951Z"
                        stroke="currentColor"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      ></path>
                    </svg>
                  </button>
                </div>
                <p className={`mt-2 text-center text-sm text-[#687585] px-4 ${isSignSimpli ? "md:px-[58px]" : ""}`}>
                  Step 2 of 2: We’ve sent a 6-digit verification code to{" "}
                  <span className="text-blue-600">{phoneNumber || "000-000-0000"}</span>— the code expires in 10 minutes
                </p>
              </div>

              {/* OTP Inputs */}
              <div className={`flex justify-center gap-2 ${isSignSimpli ? "mt-4" : "mt-6"}`}>
                {otp.map((digit, i) => (
                  <input
                    key={i}
                    id={`otp-input-${i}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleChange(e, i)}
                    onKeyDown={(e) => handleKeyDown(e, i)}
                    onPaste={handlePaste}
                    className={`w-10 h-10 border rounded-md text-center text-xl font-semibold focus:ring-2 focus:ring-orange-500 ${
                      error ? "border-red-500" : "border-gray-300"
                    }`}
                  />
                ))}
              </div>
            </>
          )}

          {/* Error message (conditional) */}
          {error && <p className="mt-4 text-center text-sm text-red-500 font-medium">{error}</p>}

          {/* Verify Button (changes label based on step) */}
          <div className="mt-6 flex justify-center">
            <button
              id={step === 1 ? "btnVerifyPhone" : "btnVerifyOtp"}
              type="button"
              onClick={step === 1 ? onSendCode : onVerifyOtp}
              disabled={
                isLoading ||
                (step === 1 && !isValidPhoneNumber(phoneWithCountryCode)) ||
                (step === 2 && otp.join("").length < 6) ||
                isEditingPhone
              }
              className={`w-full max-w-xs py-4 text-white leading-[120%] tracking-[2%] ${isSignSimpli ? "md:text-[18px] mb-4" : "md:text-xl"} font-semibold rounded-[100px] bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 uppercase transition-colors duration-1000 ease-in-out disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isLoading ? "LOADING..." : step === 1 ? "VERIFY PHONE" : "VERIFY PHONE"}
            </button>
          </div>

          {step === 1 && (
            <p className="text-[#687585] text-[8px] md:text-[10px] font-normal text-center mt-4">
              *By providing my phone number, I consent to receive informational messages and alerts from WORKSIMPLI
              SOFTWARE LLC. Message frequency varies. Message & data rates may apply. Reply STOP to unsubscribe at any
              time.
            </p>
          )}

          {/* Resend Code (Only visible in Step 2) */}
          {step === 2 && (
            <OtpResendTimer duration={100} onResend={handleResend} btnText="Resend code" />
            // <div className="mt-3 text-center">
            //   <button
            //     className="text-blue-600 text-sm font-medium hover:underline"
            //     onClick={() => {
            //       setError(""); // clear error on resend
            //       toSetOtp(Array(6).fill("")); // clear OTP input
            //       onSendCode(); // Resend code using the existing function
            //     }}
            //   >
            //     Resend code
            //   </button>
            // </div>
          )}

          {/* Info Footer (using your original SVG) */}
        </div>
        {Footer}
      </div>
    </>
  );
};

export default VerifyByPhone;
